import { useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Sparkles } from "lucide-react";
import { SeoResultCard, SeoResult } from "@/components/SeoResultCard";
import { Link } from "react-router-dom";

const schema = z.object({
  product_name: z.string().trim().min(1, "Product name required").max(200),
  product_url: z
    .string()
    .trim()
    .max(500)
    .url("Please enter a valid URL (https://...)")
    .optional()
    .or(z.literal("").transform(() => undefined)),
  description: z.string().trim().max(2000).optional(),
  category: z.string().trim().max(100).optional(),
  language: z.string().trim().max(40),
});

const CATEGORIES = ["Apparel", "Electronics", "Gadgets", "Home & Garden", "Furniture", "Kitchen & Dining", "Beauty", "Health", "Personal Care", "Sports", "Outdoors", "Fitness", "Toys", "Baby Products", "Kids & Nursery", "Pet Supplies", "Food & Beverage", "Grocery", "Jewelry", "Watches", "Accessories", "Bags & Luggage", "Footwear", "Books", "Education", "Stationery & Office", "Arts & Crafts", "Music & Instruments", "Automotive", "Tools & Hardware", "Industrial & Scientific", "Software & Digital", "Gaming", "Mobile & Computers", "Cameras & Photography", "Travel", "Wedding & Events", "Garden & Outdoor", "Cleaning & Household", "Wellness & Supplements", "Other"];
const LANGUAGES = ["English", "Spanish", "French", "German", "Italian", "Portuguese", "Dutch", "Japanese"];

export default function Generate() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SeoResult | null>(null);
  const [productName, setProductName] = useState("");

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const parsed = schema.safeParse({
      product_name: fd.get("product_name"),
      product_url: fd.get("product_url") || undefined,
      description: fd.get("description") || undefined,
      category: fd.get("category") || undefined,
      language: fd.get("language") || "English",
    });
    if (!parsed.success) {
      toast.error(parsed.error.errors[0].message);
      return;
    }

    setLoading(true);
    setResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("generate-seo", { body: parsed.data });
      if (error) throw error;
      if (data?.error === "limit_reached") {
        toast.error(data.message ?? "Limit reached", {
          action: { label: "Upgrade", onClick: () => (window.location.href = "/settings") },
        });
        return;
      }
      if (data?.error) throw new Error(data.error);
      setResult(data.result);
      setProductName(parsed.data.product_name);
      toast.success("SEO content generated!");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Generation failed";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Generate SEO</h1>
        <p className="text-muted-foreground">Fill in your product details and let AI craft your listing.</p>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <Card className="bg-gradient-card border-border/50">
          <CardHeader>
            <CardTitle>Product details</CardTitle>
            <CardDescription>Only the product name is required.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="product_name">Product name *</Label>
                <Input id="product_name" name="product_name" required placeholder="Wireless Noise-Cancelling Headphones" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="product_url">Product link (optional)</Label>
                <Input
                  id="product_url"
                  name="product_url"
                  type="url"
                  inputMode="url"
                  placeholder="https://yourstore.com/products/your-product"
                />
                <p className="text-xs text-muted-foreground">
                  Paste your product page URL so AI can tailor SEO to your actual listing for more accurate results.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="category">Category</Label>
                <Select name="category" defaultValue="">
                  <SelectTrigger><SelectValue placeholder="Select a category" /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="language">Target language</Label>
                <Select name="language" defaultValue="English">
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="description">Description</Label>
                <Textarea id="description" name="description" rows={5} placeholder="Optional: features, materials, audience..." />
              </div>
              <Button type="submit" className="w-full bg-gradient-primary shadow-glow" disabled={loading}>
                {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Generating...</> : <><Sparkles className="h-4 w-4 mr-2" />Generate SEO</>}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div>
          {loading && (
            <Card className="bg-gradient-card border-border/50 h-full flex items-center justify-center min-h-[400px]">
              <div className="text-center space-y-4">
                <div className="h-16 w-16 rounded-full bg-gradient-primary mx-auto flex items-center justify-center shadow-glow animate-pulse">
                  <Sparkles className="h-8 w-8 text-primary-foreground" />
                </div>
                <div>
                  <p className="font-semibold">AI is writing your listing...</p>
                  <p className="text-sm text-muted-foreground">Optimizing for clicks and rankings</p>
                </div>
              </div>
            </Card>
          )}
          {!loading && result && <SeoResultCard result={result} productName={productName} />}
          {!loading && !result && (
            <Card className="bg-gradient-card border-border/50 border-dashed h-full flex items-center justify-center min-h-[400px]">
              <div className="text-center text-muted-foreground p-6">
                <Sparkles className="h-10 w-10 mx-auto mb-3 opacity-40" />
                <p className="font-medium">Your SEO result will appear here</p>
                <p className="text-sm mt-1">Need ideas? Check your <Link to="/history" className="text-primary underline">history</Link>.</p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
